import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent } from "@tellescope/ui";
import { Edit, Clock, Plus } from "lucide-react";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { METRIC_ORDER, METRICS } from "./metrics";
import { TodaySummary } from "./TodaySummary";
import { NutritionTargetsEditor } from "./NutritionTargetsEditor";
import { WeightHeroCard } from "./WeightHeroCard";
import { MetricTrendCard } from "./MetricTrendCard";
import { useMetricLabel } from "./useMetricLabel";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

interface TrackerDashboardProps {
  state: TrackerState;
}

const NON_WEIGHT_METRIC_IDS = METRIC_ORDER.filter((id) => id !== "weight");

export const TrackerDashboard = ({ state }: TrackerDashboardProps) => {
  const { t } = useTranslation("portal");
  const { metricLabel } = useMetricLabel();
  const [isEditingTargets, setIsEditingTargets] = useState(false);

  const hasAnyEntries = state.weightEntries.length > 0 || state.foodEntries.length > 0 || state.waterEntries.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={state.goToHistory}>
          <Clock className="h-3.5 w-3.5 mr-1.5" />
          {t("History")}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => state.goToLogEntry()}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {t("Log Entry")}
        </Button>
      </div>

      {hasAnyEntries ? (
        <div className="space-y-3">
          <WeightHeroCard
            weightEntries={state.weightEntries}
            foodEntries={state.foodEntries}
            weightUnit="lb"
            onClick={() => state.goToMetricDetail("weight")}
          />
          <TodaySummary totals={state.todayTotals} targets={state.targets} />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">
              {t("Trends")}
            </p>
            <div className="space-y-2">
              {NON_WEIGHT_METRIC_IDS.map((metricId) => (
                <MetricTrendCard
                  key={metricId}
                  metric={METRICS[metricId]}
                  metricLabel={metricLabel(metricId)}
                  weightEntries={state.weightEntries}
                  foodEntries={state.foodEntries}
                  waterEntries={state.waterEntries}
                  onClick={() => state.goToMetricDetail(metricId)}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            {t("No entries logged yet. Tap Log Entry above to start tracking.")}
          </CardContent>
        </Card>
      )}

      {isEditingTargets ? (
        <NutritionTargetsEditor
          targets={state.targets}
          onSave={state.setTargetsAsync}
          isPending={state.isSavingTargets}
          onClose={() => setIsEditingTargets(false)}
        />
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setIsEditingTargets(true)} data-test="edit-targets-toggle">
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          {t("Edit targets")}
        </Button>
      )}
    </div>
  );
};
